import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, mediaUrl, money, apiErr } from "@/lib/api";
import { Button, Empty, StatusPill, Badge, Input } from "@/lib/ui";

export default function MyProducts() {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [reduce, setReduce] = useState({}); // {pid: "newQty"}

  const load = () => api.get("/seller/products").then(({ data }) => setProducts(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submitReduce = async (p) => {
    const q = parseInt(reduce[p.id], 10);
    if (Number.isNaN(q) || q < 0) { toast.error("Enter a valid quantity"); return; }
    if (q >= p.quantity) { toast.error(`Enter a quantity lower than ${p.quantity} ${p.primary_unit}`); return; }
    const soldQty = p.quantity - q, value = soldQty * p.primary_rate;
    if (!window.confirm(`Mark ${soldQty} ${p.primary_unit} as sold (₹${value.toLocaleString("en-IN")})? Stock becomes ${q} ${p.primary_unit}.`)) return;
    try {
      const { data } = await api.post(`/seller/products/${p.id}/inventory`, { quantity: q });
      toast.success(`${soldQty} ${p.primary_unit} sold · ₹${value.toLocaleString("en-IN")} added. Total sold till date: ₹${Math.round(data.total_sold_value).toLocaleString("en-IN")}`);
      setReduce((r) => ({ ...r, [p.id]: "" })); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggle = async (id) => {
    try { await api.post(`/seller/products/${id}/toggle`); toast.success("Updated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/seller/products/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const filtered = products.filter((p) => p.product.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-head text-2xl font-extrabold text-slate-900">My Products</h1>
        <Button data-testid="myproducts-add" onClick={() => nav("/seller/app/products/new")}><i className="fa-solid fa-plus" /> Add Product</Button>
      </div>
      <div className="mt-4 max-w-xs">
        <Input placeholder="Search my products…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="myproducts-search" />
      </div>

      {loading ? <p className="mt-6 text-slate-400">Loading…</p> :
        filtered.length === 0 ? <div className="mt-6"><Empty text="No products yet — add your first listing" icon="fa-box-open" /></div> : (
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p) => (
              <div key={p.id} data-testid={`myproduct-${p.id}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="relative aspect-video bg-slate-100">
                  <img src={mediaUrl(p.catalogue_image)} alt={p.product} className="h-full w-full object-cover" />
                  <div className="absolute left-2 top-2"><StatusPill status={p.approval === "approved" ? p.status : p.approval} /></div>
                  {p.discount > 0 && <span className="absolute right-2 top-2 rounded-full bg-orange-600 px-2 py-0.5 text-xs font-bold text-white">{p.discount}% OFF</span>}
                </div>
                <div className="p-3">
                  <p className="line-clamp-1 font-head font-bold text-slate-900">{p.product}</p>
                  <p className="text-xs text-slate-500">{p.category} · {p.size}</p>
                  <p className="mt-1 text-sm font-semibold">{money(p.primary_rate)}/{p.primary_unit}</p>
                  <p className="text-xs text-slate-500" data-testid={`stock-${p.id}`}>In stock: <b className="text-slate-800">{p.quantity} {p.primary_unit}</b></p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {p.manufacturer_stock && <Badge tone="blue">Mfg Stock</Badge>}
                    {p.pan_india && <Badge tone="orange">Pan India</Badge>}
                  </div>
                  {p.approval === "rejected" && p.reject_reason && (
                    <p className="mt-1 text-xs text-red-600">Rejected: {p.reject_reason}</p>
                  )}
                  {p.approval === "approved" && (
                    <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50 p-2" data-testid={`reduce-box-${p.id}`}>
                      <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700">Reduce stock (sold)</p>
                      <div className="flex gap-2">
                        <input type="number" min={0} max={Math.max(p.quantity - 1, 0)} inputMode="numeric" placeholder={`New qty (${p.primary_unit})`}
                          value={reduce[p.id] ?? ""} onChange={(e) => setReduce((r) => ({ ...r, [p.id]: e.target.value.replace(/\D/g, "") }))}
                          data-testid={`reduce-input-${p.id}`} className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm" />
                        <Button onClick={() => submitReduce(p)} disabled={!reduce[p.id]} data-testid={`reduce-submit-${p.id}`}>Submit</Button>
                      </div>
                      {reduce[p.id] !== undefined && reduce[p.id] !== "" && parseInt(reduce[p.id], 10) < p.quantity && (
                        <p className="mt-1 text-[11px] text-emerald-700">Sold: {p.quantity - parseInt(reduce[p.id], 10)} {p.primary_unit} × {money(p.primary_rate)} = <b>{money((p.quantity - parseInt(reduce[p.id], 10)) * p.primary_rate)}</b></p>
                      )}
                    </div>
                  )}
                  <div className="mt-3 flex gap-2">
                    {p.approval !== "approved" && <Button variant="outline" className="flex-1" onClick={() => nav(`/seller/app/products/${p.id}/edit`)} data-testid={`edit-${p.id}`}>Edit</Button>}
                    {p.approval === "approved" && (
                      <Button variant="ghost" className="flex-1" onClick={() => toggle(p.id)} data-testid={`toggle-${p.id}`}>
                        {p.status === "active" ? "Deactivate" : "Activate"}
                      </Button>
                    )}
                    <Button variant="ghost" onClick={() => del(p.id)} data-testid={`delete-${p.id}`}><i className="fa-solid fa-trash text-red-500" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
